/* eslint-disable camelcase */
/* eslint-disable radix */
import { createKey, getElementData } from "shared/utils";
import { findByFather, findElement } from "store/selectors/pageSelector";
import duplicateColumn from "store/actions/duplicateColumn";
import duplicateSection from "store/actions/duplicateSection";
import Request from "shared/Request";
import { getComponentDefinition } from "shared/utils/getElementData";

export const loadPage = (pageId) => {
  return Request.get(`/pages/${pageId}`);
};

export const setPage = (page) => {
  return {
    type: "SET_PAGE",
    payload: page,
  };
};

export const updateElement = (element) => {
  return {
    type: "UPDATE_ELEMENT",
    payload: element,
  };
};

export const moveElement = (cords) => {
  return {
    type: "MOVE_ELEMENT",
    payload: cords,
  };
};

export const deleteElement = (elementKey) => ({
  type: "DELETE_ELEMENT",
  payload: elementKey,
});

export const clearColumn = (columnKey) => ({
  type: "CLEAR_COLUMN",
  payload: columnKey,
});

export const clearSection = (sectionKey) => {
  const column = createColumn(sectionKey);
  return {
    type: "CLEAR_SECTION",
    payload: {
      key: sectionKey,
      column,
    },
  };
};

/**
 * Inserts a new element in the page.
 *
 * @param {Object} elementObj Properties of the desired element that will be added to the page.
 * @param {String} elementObj.component Component implementation of the element
 * @param {String} elementObj.version Version of the component. Defaults to "default"
 * @param {String} elementObj.parent_id Target where the element will be added
 * @param {Number} elementObj.position Positiion where the element is being added.
 * @param {Object} elementObj.props Properties for the element being added
 *
 */
export const addElement = (elementObj) => {
  const element = createElement(elementObj);
  return {
    type: "ADD_ELEMENTS",
    payload: [element],
  };
};

/**
 * Adds a section at the provided position. The section comes with
 * a column without any elements.
 *
 * @param {Integer} position position where the section will be added
 */
export const addSection = (position) => {
  const section = createSection(position);
  const column = createColumn(section.key);
  return {
    type: "ADD_ELEMENTS",
    payload: [section, column],
  };
};

/**
 * Adds a column to the given section while updating other columns
 * within it to reflect changes such as ordering and weight (column size
 * in a css grid system.)
 *
 * @param {String} sectionID Key for the section having a column added
 * @param {Integer} position position where the column will be added
 * @params
 */
export const addColumn = (sectionID, pos = null, props = {}) => {
  return (dispatch, getState) => {
    const state = getState();
    let x = 0;
    let i = 0;
    // Find all columns within a given section and order them according
    // to their weight, so we can set the new column weight properly.
    const columns = findByFather(state.page.components, sectionID).sort(
      (a, b) => (a.props.weight > b.props.weight ? -1 : 0)
    );
    const newColumns = [...columns];
    const lastColumn = columns[columns.length - 1];

    // Them we create a new column notation
    const position = pos || columns.length + 1;
    const positionW = columns.length + 1;
    let weight = parseInt(12 / positionW);
    if (lastColumn.props.weight < weight) {
      // eslint-disable-next-line prefer-destructuring
      weight = lastColumn.props.weight;
    }

    // Adjust weights of other columns within the section
    while (i < weight) {
      x = 0;
      while (x < columns.length && i < weight) {
        const w = newColumns[x].props.weight - 1;
        newColumns[x].props.weight = w > 1 ? w : 1;
        x += 1;
        i += 1;
      }
    }

    const column = createColumn(sectionID, { weight, position, ...props });

    return dispatch({
      type: "ADD_COLUMN",
      payload: {
        columns: newColumns,
        newColumn: column,
      },
    });
  };
};

/**
 * Will dispatch an update on multiple columns in order to
 * resize them.
 *
 * @param {String} parentID Key of the parent Section
 * @param {Array} columnsToBeUpdated Array of objects with columns properties to be updated
 */
export const resizeColumns = (parentID, columnsToBeUpdated) => {
  return (dispatch, getState) => {
    const state = getState();
    const columns = findByFather(state.page.components, parentID);
    const newColumns = columns.map((column) => {
      const updatedObj = columnsToBeUpdated.find(
        (col) => col.key === column.key
      );
      if (updatedObj) {
        return {
          ...column,
          props: {
            ...column.props,
            weight: updatedObj.weight,
          },
        };
      }
      return column;
    });
    return dispatch({
      type: "UPDATE_ELEMENTS",
      payload: newColumns,
    });
  };
};

export const deleteColumn = (columnKey) => {
  return (dispatch, getState) => {
    const state = getState();
    const targetColumn = state.page.components.find(
      (col) => col.key === columnKey
    );
    const newColumns = findByFather(
      state.page.components,
      targetColumn.parent_id
    )
      .filter((cmp) => cmp.key !== columnKey)
      .sort((a, b) => {
        if (a.props.weight < b.props.weight) {
          return -1;
        }
        return a.props.weight > b.props.weight ? 1 : 0;
      });
    let i = 0;
    let x = 0;
    while (i < targetColumn.props.weight) {
      x = 0;
      while (x < newColumns.length && i < targetColumn.props.weight) {
        newColumns[x].props.weight += 1;
        x += 1;
        i += 1;
      }
    }
    return dispatch({
      type: "DELETE_COLUMN",
      payload: {
        column: targetColumn.key,
        newColumns,
      },
    });
  };
};

/**
 * This will create a new section in the given position
 *
 * @param {Integer} position initial position of the section being created
 * @return {Object} an object representing an empty section
 */
const createSection = (position) => {
  const props = getElementData("Section");
  return {
    component: "Section",
    version: "1",
    key: createKey(),
    position,
    parent_id: null,
    props,
  };
};

/**
 * Creates the notation of a new column without any components.
 *
 * @param {String} fatherKey Parent key
 * @param {Object} options Column options that will be used to generate it's
 * properties
 *
 * @return {Object} Column object that will be added in redux
 */
const createColumn = (fatherKey, options = {}) => {
  const cmpDef = getComponentDefinition("Column");
  const props = {
    weight: 12,
    position: 1,
    ...getElementData("Column", `V${cmpDef.version}`),
    ...options,
  };

  const element = {
    component: "Column",
    parent_id: fatherKey,
    position: props.position,
    version: cmpDef.version,
    key: createKey(),
    props,
  };

  return element;
};

/**
 * Creates the notation of a new element being added to the page.
 *
 * @param {Object} element new element properties
 */
const createElement = (element) => {
  return {
    ...element,
    key: createKey(),
  };
};

export const duplicateElement = (elementKey) => {
  return (dispatch, getState) => {
    const state = getState();
    const targetElement = findElement(state, elementKey);
    switch (targetElement.component) {
      case "Section":
        duplicateSection(targetElement, state, dispatch);
        break;
      case "Column": {
        duplicateColumn(targetElement, state, dispatch);
        break;
      }
      default: { 
        const { key, position, parent_id, ...originElement } = targetElement;
        const duplicatedElement = {
          position: parseInt(position) + 1,
          key: createKey(),
          parent_id,
          ...originElement,
        };
        dispatch(addElement(duplicatedElement));
        break;
      }
    }
  };
};

const cloneChildrenElements = (state, originParent, destParent) => {
  const elements = [];
  const children = findByFather(state, originParent);
  children.forEach((child) => {
    const { key, parent_id, components, ...originalChild } = child;
    const newChild = {
      parent_id: destParent,
      key: createKey(),
      ...originalChild,
    };
    const clonedSubChildren = cloneChildrenElements(state, key, newChild.key);
    elements.push([newChild, ...clonedSubChildren]);
  });
  return elements.flat(Infinity);
};
